import styles from "./EditProfileModal.module.scss";
import { UploadLogo } from "@/frontend/shared/ui/UploadLogo/UploadLogo";
import { useState } from "react";
import { Typography } from "@/frontend/shared/ui/Typography";
import { Input } from "@/frontend/shared/ui/Input";
import { Textarea } from "@/frontend/shared/ui/Textarea/Textarea";
import { EditSectionModal } from "../EditSectionModal/EditSectionModal";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { patchUpdateRevisionLogo } from "../../model/services/patchUpdateRevisionLogo";
import { deleteRevisionLogo } from "../../model/services/deleteRevisionLogo";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { AxiosError } from "axios";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { postGenerateRevisionData } from "../../model/services/postGenerateRevisionData";
import clsx from "clsx";
import { TCompanyDetails } from "@/frontend/features/Company/model/types/types";
import { TProfileData, TProfileDataErrors } from "./model/types/types";
import { postRequestWebsiteUpdate } from "../../model/services/postRequestWebsiteUpdate";
import { validateLogo } from "@/frontend/shared/lib/validations/validateLogo";
import { validateText } from "@/frontend/shared/lib/validations/validateText";
import { validateWebsite } from "@/frontend/shared/lib/validations/validateWebsite";
import { validateEmail } from "@/frontend/shared/lib/validations/validateEmail";
import { validatePhone } from "@/frontend/shared/lib/validations/validatePhone";
import { validateTwitterUrl } from "@/frontend/shared/lib/validations/validateTwitterUrl";
import { validateLinkedInUrl } from "@/frontend/shared/lib/validations/validateLinkedInUrl";
import { validateFacebookUrl } from "@/frontend/shared/lib/validations/validateFacebookUrl";
import useLockBodyScroll from "@/frontend/shared/hooks/useLockBodyScroll";

type TEditProfileModalProps = {
  isOpen: boolean;
  onClose: () => void;
  data?: TCompanyDetails;
  triggerRefetch: () => void;
};

enum ESections {
  PROFILE = "Profile",
  WEBSITE_UPDATE = "Website Update"
}

export function EditProfileModal({ data, isOpen, onClose, triggerRefetch }: TEditProfileModalProps) {
  const { name, website, headline, logoUrl, email, phone, twitterUrl, linkedInUrl, facebookUrl } = data || {};
  const [isConfirmationModalShown, setIsConfirmationModalShown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDiscardChangesModalShown, setIsDiscardChangesModalShown] = useState(false);
  const initialEditProfileData: TProfileData = {
    name: name || "",
    website: website || "",
    headline: headline || "",
    logo: logoUrl || "",
    email: email || "",
    phone: phone || "",
    twitterUrl: twitterUrl || "",
    linkedInUrl: linkedInUrl || "",
    facebookUrl: facebookUrl || ""
  };

  const [editProfileData, setEditProfileData] = useState(initialEditProfileData);
  const [section, setSection] = useState<ESections>(ESections.PROFILE);
  const [websiteExplanation, setWebsiteExplanation] = useState<string>("");
  const [websiteValue, setWebsiteValue] = useState<string>(website || "");
  const [inputErrors, setInputErrors] = useState<TProfileDataErrors>({
    name: "",
    website: "",
    headline: "",
    email: "",
    phone: "",
    twitterUrl: "",
    linkedInUrl: "",
    facebookUrl: ""
  });

  useLockBodyScroll(isOpen);

  async function onUploadLogo({ v }: { v: string }) {
    setEditProfileData({ ...editProfileData, logo: v });
  }

  async function onDeleteLogo() {
    setEditProfileData({ ...editProfileData, logo: "" });
    setIsConfirmationModalShown(false);
  }

  const content =
    section === ESections.PROFILE ? (
      <div className={styles.container}>
        <div className={styles.modalSection}>
          <Typography variant="label-4" color="greyscale700" className={styles.label}>
            Logo
          </Typography>
          <UploadLogo
            onUpload={(v) => onUploadLogo({ v })}
            image={editProfileData.logo}
            onRemove={() => setIsConfirmationModalShown(true)}
            text="Upload Logo"
            imageStyle={styles.logo}
          />
        </div>
        <div className={styles.modalSection}>
          <Typography variant="label-4" color="greyscale700">
            Name
          </Typography>
          <Input
            type="text"
            value={editProfileData.name}
            onChange={(e) => {
              setEditProfileData({ ...editProfileData, name: e.target.value });
              setInputErrors({ ...inputErrors, name: "" });
            }}
            error={inputErrors.name}
          />
        </div>
        <div className={styles.modalSection}>
          <Typography variant="label-4" color="greyscale700">
            Headline
          </Typography>
          <Textarea
            value={editProfileData.headline}
            onChange={(e) => {
              setEditProfileData({ ...editProfileData, headline: e.target.value });
              setInputErrors({ ...inputErrors, headline: "" });
            }}
            rows={2}
            error={inputErrors.headline}
          />
        </div>
        <div className={styles.modalSection}>
          <Typography variant="label-4" color="greyscale700">
            Website
          </Typography>
          <Input
            type="text"
            className={clsx(inputErrors.website && styles.hasError)}
            value={editProfileData.website || "No website"}
            onChange={() => null}
            error={inputErrors.website}
            inputClassName={styles.websiteInput}
            disabled
          />
          <Typography variant="body-3" color="greyscale500">
            This information will help the system provide more accurate and relevant answers based on your website.
          </Typography>
          <NormalButton
            className={styles.requestButton}
            size="small"
            variant="secondary"
            onClick={() => setSection(ESections.WEBSITE_UPDATE)}
          >
            Request Website Update
          </NormalButton>
        </div>
        <div className={styles.modalSection}>
          <Typography variant="label-4" color="greyscale700">
            Email
          </Typography>
          <Input
            type="text"
            value={editProfileData.email}
            onChange={(e) => {
              setEditProfileData({ ...editProfileData, email: e.target.value });
              setInputErrors({ ...inputErrors, email: "" });
            }}
            error={inputErrors.email}
          />
        </div>
        <div className={styles.modalSection}>
          <Typography variant="label-4" color="greyscale700">
            Phone
          </Typography>
          <Input
            type="text"
            value={editProfileData.phone}
            onChange={(e) => {
              setEditProfileData({ ...editProfileData, phone: e.target.value });
              setInputErrors({ ...inputErrors, phone: "" });
            }}
            error={inputErrors.phone}
          />
        </div>
        <div className={styles.modalSection}>
          <Typography variant="label-4" color="greyscale700">
            Twitter
          </Typography>
          <Input
            type="text"
            value={editProfileData.twitterUrl}
            onChange={(e) => {
              setEditProfileData({ ...editProfileData, twitterUrl: e.target.value });
              setInputErrors({ ...inputErrors, twitterUrl: "" });
            }}
            error={inputErrors.twitterUrl}
          />
        </div>
        <div className={styles.modalSection}>
          <Typography variant="label-4" color="greyscale700">
            Facebook
          </Typography>
          <Input
            type="text"
            value={editProfileData.facebookUrl}
            onChange={(e) => {
              setEditProfileData({ ...editProfileData, facebookUrl: e.target.value });
              setInputErrors({ ...inputErrors, facebookUrl: "" });
            }}
            error={inputErrors.facebookUrl}
          />
        </div>
        <div className={styles.modalSection}>
          <Typography variant="label-4" color="greyscale700">
            LinkedIn
          </Typography>
          <Input
            type="text"
            value={editProfileData.linkedInUrl}
            onChange={(e) => {
              setEditProfileData({ ...editProfileData, linkedInUrl: e.target.value });
              setInputErrors({ ...inputErrors, linkedInUrl: "" });
            }}
            error={inputErrors.linkedInUrl}
          />
        </div>
      </div>
    ) : (
      <div className={styles.websiteUpdateContainer}>
        <div className={styles.modalSection}>
          <Typography variant="label-4" color="greyscale700" className={styles.label}>
            Website
          </Typography>
          <Input
            type="text"
            className={clsx(inputErrors.website && styles.hasError)}
            value={websiteValue}
            onChange={(e) => setWebsiteValue(e.target.value)}
            error={inputErrors.website}
          />
        </div>
        <Typography variant="label-4" color="greyscale700" className={styles.label}>
          Explanation
        </Typography>
        <Textarea
          className={styles.textarea}
          value={websiteExplanation}
          onChange={(e) => setWebsiteExplanation(e.target.value)}
        />
      </div>
    );

  function onCloseModal() {
    if (section === ESections.WEBSITE_UPDATE) {
      setSection(ESections.PROFILE);
      setWebsiteValue(website || "");
      setWebsiteExplanation("");
      setIsDiscardChangesModalShown(false);
    } else {
      onClose();
      setEditProfileData(initialEditProfileData);
      setIsDiscardChangesModalShown(false);
    }
  }

  const isLogoChanged = editProfileData.logo !== logoUrl && editProfileData.logo !== "";

  function findChangedValues() {
    const changed = {
      ...(editProfileData.name !== initialEditProfileData.name && { name: editProfileData.name }),
      ...(editProfileData.website !== initialEditProfileData.website && { website: editProfileData.website }),
      ...(editProfileData.headline !== initialEditProfileData.headline && { headline: editProfileData.headline }),
      ...(editProfileData.logo !== initialEditProfileData.logo && { logo: editProfileData.logo }),
      ...(editProfileData.email !== initialEditProfileData.email && { email: editProfileData.email }),
      ...(editProfileData.phone !== initialEditProfileData.phone && { phone: editProfileData.phone }),
      ...(editProfileData.twitterUrl !== initialEditProfileData.twitterUrl && {
        twitterUrl: editProfileData.twitterUrl
      }),
      ...(editProfileData.linkedInUrl !== initialEditProfileData.linkedInUrl && {
        linkedInUrl: editProfileData.linkedInUrl
      }),
      ...(editProfileData.facebookUrl !== initialEditProfileData.facebookUrl && {
        facebookUrl: editProfileData.facebookUrl
      })
    };

    const formatted = Object.fromEntries(
      Object.entries(changed).map(([key, value]) => {
        if (value === "") {
          return [key, null];
        }
        return [key, value];
      })
    );

    return formatted;
  }

  function validate() {
    const { name, website, headline, email, phone, twitterUrl, linkedInUrl, facebookUrl } = editProfileData;

    const errors = {
      name: validateText({ text: name, fieldName: "Name", maxLength: 70 }),
      website: validateWebsite({ website, required: false, fieldName: "Website URL", maxLength: 255 }),
      headline: validateText({ text: headline, fieldName: "Headline", maxLength: 200, minLength: 5 }),
      email: validateEmail({ email, required: false }),
      phone: validatePhone({ phone, required: false }),
      twitterUrl: validateTwitterUrl({ twitterUrl, required: false }),
      linkedInUrl: validateLinkedInUrl({ linkedInUrl, required: false }),
      facebookUrl: validateFacebookUrl({ facebookUrl, required: false })
    };
    setInputErrors(errors);

    return !Object.values(errors).some(Boolean);
  }

  async function onSave() {
    if (!validate()) {
      snackbarConstructor("Please correct the errors before saving.", "error");
      return;
    }

    if (section === ESections.PROFILE && editProfileData.name) {
      setIsLoading(true);
      try {
        if (findChangedValues()) {
          const data = await postGenerateRevisionData({ data: findChangedValues() });
          const allowedKeys = Object.keys(initialEditProfileData);
          const filteredData = Object.keys(data)
            .filter((key) => allowedKeys.includes(key as keyof TProfileData))
            .reduce((obj, key) => {
              obj[key as keyof TProfileData] = data[key] || "";
              return obj;
            }, {} as TProfileData);

          setEditProfileData({ ...filteredData, logo: editProfileData.logo });
          if (isLogoChanged) {
            await patchUpdateRevisionLogo({ logo: editProfileData.logo });
          } else if (editProfileData.logo === "" && logoUrl) {
            await deleteRevisionLogo();
          }
        }
        snackbarConstructor("Data updated successfully", "normal");
        triggerRefetch();
        onClose();
      } catch (e) {
        reportErrorToSentry(e as AxiosError, "EDIT_PROFILE_MODAL: /founder-revisions/generate-revision-data");
        reportErrorToSentry(e as AxiosError, "EDIT_PROFILE_MODAL: /founder-revisions/logo");
        snackbarConstructor("Error updating data", "error");
      } finally {
        setIsLoading(false);
      }
    }

    if (section === ESections.WEBSITE_UPDATE) {
      if (!websiteValue) {
        snackbarConstructor("Website is required", "error");
        return;
      }
      setIsLoading(true);
      try {
        await postRequestWebsiteUpdate({ desiredWebsite: websiteValue, additionalNotes: websiteExplanation });
        snackbarConstructor("Request sent successfully", "normal");
      } catch (e) {
        reportErrorToSentry(e as AxiosError, "EDIT_PROFILE_MODAL: /founder-revisions/request-website-update");
        snackbarConstructor("Error sending request", "error");
      } finally {
        setIsLoading(false);
        setSection(ESections.PROFILE);
        setWebsiteValue(website || "");
        setWebsiteExplanation("");
      }
    }
  }

  function isChangedValues() {
    const changes = findChangedValues();
    return Object.keys(changes).length > 0;
  }

  function isChangedWebsiteData() {
    if (websiteValue !== website || websiteExplanation.length > 0) return true;
  }

  function onDiscardChanges() {
    if (isChangedValues() && section === ESections.PROFILE) {
      return setIsDiscardChangesModalShown(true);
    } else if (section === ESections.WEBSITE_UPDATE && isChangedWebsiteData()) {
      return setIsDiscardChangesModalShown(true);
    } else if (section === ESections.WEBSITE_UPDATE && !isChangedWebsiteData()) {
      return onCloseModal();
    }

    return onClose();
  }

  return (
    <EditSectionModal
      isOpen={isOpen}
      onClose={onDiscardChanges}
      title={section === ESections.PROFILE ? "Edit Profile" : "Request Website Update"}
      width="600px"
      onSave={onSave}
      saveButtonName={section === ESections.WEBSITE_UPDATE ? "Submit" : "Save"}
      isLoading={isLoading}
    >
      {content}
      <ConfirmationModal
        isOpen={isConfirmationModalShown}
        onClose={() => setIsConfirmationModalShown(false)}
        onConfirm={onDeleteLogo}
        title="Remove Logo"
        content="Are you sure you want to remove your logo?"
        confirmButtonText="Remove"
        cancelButtonText="No Thanks"
        dangerConfirm
      />
      <ConfirmationModal
        isOpen={isDiscardChangesModalShown}
        onClose={() => setIsDiscardChangesModalShown(false)}
        onConfirm={onCloseModal}
        title="Discard Changes"
        content="Are you sure you want to discard your changes?"
        confirmButtonText="Discard"
        cancelButtonText="No Thanks"
      />
    </EditSectionModal>
  );
}
